import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useSiteLocations } from "@/api/hooks/locations";
import { useAddLocationToDA } from "@/api/hooks/siteDALocations";

interface Props {
  open: boolean;
  onClose: () => void;
  siteId: string;
  daId: string;
  daCode: string;
  /** location_ids already enrolled in this DA — excluded from the
   * picker. */
  alreadyEnrolledIds: Set<string>;
}

/** Picker over the site's existing locations. Creating a new location
 * happens on the site detail's Locations card (above the DA tabs) so
 * one location can be enrolled in multiple disease areas without
 * re-entering its address. */
export function AddLocationToDADialog({
  open,
  onClose,
  siteId,
  daId,
  daCode,
  alreadyEnrolledIds,
}: Props) {
  const locations = useSiteLocations(siteId);
  const enrollLocation = useAddLocationToDA(siteId, daCode);

  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const eligible = useMemo(
    () =>
      (locations.data ?? []).filter(
        (l) => l.is_active !== false && !alreadyEnrolledIds.has(l.id),
      ),
    [locations.data, alreadyEnrolledIds],
  );

  const reset = () => {
    setSelectedLocationId("");
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    if (!selectedLocationId) {
      setError("Pick a location.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await enrollLocation.mutateAsync({ daId, locationId: selectedLocationId });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enroll location");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add location to this disease area</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {eligible.length === 0 ? (
          <Typography color="text.secondary">
            No eligible locations. Add a location to this site using the
            Locations card above, then come back here to enroll it.
          </Typography>
        ) : (
          <TextField
            select
            fullWidth
            label="Location"
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            {eligible.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.display_id} · {loc.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving || eligible.length === 0 || !selectedLocationId}
        >
          {saving ? "Adding…" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
